import { TemplateElement, html, defineElement, when, classMap } from '@webtides/element-js';
import style from './page-teaser.css';
import i18n from '../../../../utils/i18n.js';
import '../../../author-chip/author-chip.js';
import '../../../button/button.js';
import '../../../chip/chip.js';
import '../../../icon/icon.js';
import '../../../relative-date/relative-date.js';

/**
 * @property {'default' || 'standalone' || 'compact'} variant
 * @property {'landscape' || 'portrait'} orientation
 * @property {boolean} highlightMobile
 * @property {boolean} highlightDesktop
 * @property {PageTeaserProperties} page
 */
export default class PageTeaser extends TemplateElement {
    constructor() {
        super({ styles: [style] });
    }

    properties() {
        return {
            variant: 'default',
            orientation: 'landscape',
            highlightMobile: false,
            highlightDesktop: false,
            page: null,
        };
    }

    template() {
        const teaserClasses = {
            '!flex-col lg:!flex-row border-0 gap-24': this.variant === 'standalone',
            'duration-200 ease-in': this.variant !== 'compact',
            'hover:shadow-mb-3': this.variant === 'default',
            'bg-white hover:shadow-[0_12px_24px_-8px_rgba(0,0,0,0.15)] duration-[400ms] ease-out':
                this.variant === 'compact',
            '!flex-row': (this.variant === 'default' && !this.highlightMobile) || this.variant === 'compact',
            '!flex-col md:!flex-row': this.variant === 'default' && this.highlightMobile,
            'lg:!flex-col': this.variant === 'default' && this.orientation === 'landscape',
            'md:!flex-col': this.variant === 'default' && this.orientation === 'portrait',
            'lg:!flex-row': this.variant === 'default' && this.highlightDesktop,
        };

        const imageClasses = {
            'rounded-2xl w-full lg:w-1/2 aspect-[4/3]': this.variant === 'standalone',
            'w-1/3 rounded-l-2xl rounded-r-none aspect-[4/3]': this.variant === 'default' && !this.highlightMobile,
            'w-1/3 rounded-l-2xl rounded-r-none aspect-square': this.variant === 'compact',
            'w-full rounded-t-2xl rounded-b-none aspect-video': this.variant === 'default' && this.highlightMobile,
            'md:w-1/3 md:rounded-l-2xl md:rounded-r-none md:aspect-[4/3] lg:w-full lg:rounded-t-2xl lg:rounded-b-none lg:aspect-video':
                this.variant === 'default' && this.orientation === 'landscape',
            'md:w-full md:rounded-t-2xl md:rounded-b-none md:aspect-video':
                this.variant === 'default' && this.orientation === 'portrait',
            'lg:w-1/2 lg:rounded-l-2xl lg:rounded-r-none lg:aspect-[4/3]':
                this.variant === 'default' && this.highlightDesktop,
        };

        const chipClasses = {
            'mb-chip-size-md': this.variant === 'standalone',
            'mb-chip-size-sm lg:mb-chip-size-md': this.variant === 'default',
            'mb-chip-size-sm': this.variant === 'compact',
        };

        const contentClasses = {
            'px-16 py-8 md:px-24 md:py-16 lg:py-24': this.variant === 'default',
            'px-fix-2xs py-fix-s': this.variant === 'compact',
        };

        const textClasses = {
            // needs to be display: -webkit-box because of the line-clamp
            'line-clamp-3 hidden md:[display:-webkit-box]': this.variant === 'default' && !this.highlightMobile,
            'line-clamp-3': this.variant === 'standalone' || (this.variant === 'default' && this.highlightMobile),
            'line-clamp-2': this.variant === 'compact',
        };

        const imageSizes =
            this.variant === 'standalone'
                ? '(min-width: 1440px) 560px, (min-width: 1024px) 42vw, 100vw'
                : this.variant === 'default' && this.highlightMobile
                  ? '(min-width: 1440px) 560px, (min-width: 768px) 45vw, 100vw'
                  : '(min-width: 1440px) 560px, (min-width: 768px) 45vw, 33vw';

        const imageHoverClasses =
            this.variant === 'compact'
                ? 'duration-[500ms] ease-out group-hover:scale-[1.03]'
                : 'duration-200 ease-in group-hover:md:scale-110';

        return html`<div
            class="relative teaser flex rounded-2xl h-full w-full border border-solid border-primary-subtle3 group transition-shadow ${classMap(
                teaserClasses,
            )}"
        >
            <div class="overflow-hidden ${classMap(imageClasses)}">
                <img
                    loading="lazy"
                    src="${this.page.image.src ?? ''}"
                    srcset="${this.page.image.srcset ?? ''}"
                    sizes="${imageSizes}"
                    alt="${this.page.image.alt ?? ''}"
                    class="h-full w-full object-cover origin-bottom transition-transform ${imageHoverClasses}"
                />
            </div>
            <div class="flex-1 flex flex-col justify-center gap-8 lg:gap-16 ${classMap(contentClasses)}">
                ${when(
                    this.page.categories[0]?.label,
                    html`
                        <mb-chip
                            class="self-start ${classMap(chipClasses)}"
                            variant="${this.page.categories[0]?.labelStyle || 'warning'}"
                            text="${this.page.categories[0]?.label}"
                        />
                    `,
                )}
                <div class="flex flex-col gap-8 lg:gap-16">
                    <h3
                        class="mb-0 text-green-medium fix-body2-medium ${
                            this.variant === 'compact' ? 'line-clamp-2' : 'lg:fix-title3-medium'
                        }"
                    >
                        ${when(
                            this.variant !== 'standalone',
                            html`
                                <a href="${this.page.link}" class="stretched-link" element="page teasers"
                                    >${this.page.title}</a
                                >
                            `,
                            this.page.title,
                        )}
                    </h3>
                    ${when(
                        this.variant !== 'compact',
                        html`<p class="mb-0 flex-1 fix-body1 text-green-light ${classMap(textClasses)}">
                            ${this.page.introText}
                        </p>`,
                    )}
                </div>
                ${when(
                    this.variant === 'standalone',
                    html`
                        <mdx-button-link variant="secondary" size="md" href="${this.page.link}" element="page teasers">
                            <span>${i18n('pageTeasers.continueReading')}</span>
                            <mdx-icon-arrow-right size="xs"></mdx-icon-arrow-right>
                        </mdx-button-link>
                    `,
                    when(
                        this.variant !== 'compact',
                        html`<div
                            class="flex items-center justify-between ${!this.highlightDesktop ? 'md:mt-auto' : ''}"
                        >
                            ${when(
                                this.page.authors?.length === 1,
                                html`<mb-author-chip class="z-10" .author="${this.page.authors[0]}"></mb-author-chip>`,
                            )}
                            ${when(
                                this.page.updateDate,
                                html`
                                    <span
                                        class="flex items-center gap-8 text-green-light fix-label1 md:fix-body3 ml-auto"
                                    >
                                        <mdx-icon-refresh size="xs"></mdx-icon-refresh>
                                        <mb-relative-date date="${this.page.updateDate}"></mb-relative-date>
                                    </span>
                                `,
                                when(
                                    this.page.publicationDate,
                                    html`
                                        <mb-relative-date
                                            date="${this.page.publicationDate}"
                                            class="text-green-light fix-label1 md:fix-body3 ml-auto"
                                        ></mb-relative-date>
                                    `,
                                ),
                            )}
                        </div>`,
                    ),
                )}
            </div>
        </div>`;
    }
}

defineElement('mb-page-teaser', PageTeaser);
